import { useState } from 'react';
import { motion } from 'framer-motion';
import useGameStore from '@/store/gameStore';
import websocketService from '@/services/websocket';
import toast from 'react-hot-toast';

function WritingPhase() {
  const { gameState, isGuesser, user } = useGameStore();
  const [clue, setClue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!gameState?.round) return null;

  const isCurrentGuesser = isGuesser();
  const hasSubmitted = user ? gameState.round.clues[user.id] !== undefined : false;
  const targetWord = !isCurrentGuesser ? gameState.round.targetWord : null;

  const handleSubmitClue = async () => {
    const trimmedClue = clue.trim();

    if (!trimmedClue) {
      toast.error('Please enter a clue');
      return;
    }

    if (trimmedClue.split(' ').length > 1) {
      toast.error('Clue must be a single word');
      return;
    }

    setIsSubmitting(true);
    websocketService.submitClue(trimmedClue);
    setIsSubmitting(false);
    setClue('');
  };

  if (isCurrentGuesser) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="text-6xl mb-4">🤔</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">You are the Guesser!</h2>
          <p className="text-gray-600 mb-6">Wait while other players write their clues...</p>

          {/* Mystery Card */}
          <div className="inline-block p-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg transform rotate-3">
            <div className="bg-white rounded-lg p-6">
              <p className="text-4xl font-bold">???</p>
              <p className="text-sm text-gray-500 mt-2">Mystery Word</p>
            </div>
          </div>

          <p className="text-sm text-gray-500 mt-6">
            Clues submitted: {Object.keys(gameState.round.clues).length} /{' '}
            {Object.keys(gameState.players).length - 1}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto"
      >
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Write a Clue!</h2>

        {/* Target Word */}
        {targetWord && (
          <div className="text-center mb-8">
            <p className="text-sm text-gray-500 mb-2">The word is:</p>
            <motion.div
              initial={{ rotateY: 180, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block p-6 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg"
            >
              <p className="text-3xl font-bold text-white">{targetWord}</p>
            </motion.div>
          </div>
        )}

        {!hasSubmitted ? (
          <div className="space-y-4">
            <div>
              <input
                type="text"
                value={clue}
                onChange={(e) => setClue(e.target.value)}
                placeholder="Enter your one-word clue..."
                maxLength={30}
                disabled={isSubmitting}
                data-testid="input-clue"
                className="input text-center text-lg font-semibold"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmitClue();
                  }
                }}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1 text-center">
                Must be a single word • No variations of the target word
              </p>
            </div>

            <button
              onClick={handleSubmitClue}
              disabled={!clue.trim() || isSubmitting}
              data-testid="btn-submit-clue"
              className="w-full btn-primary"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Clue'}
            </button>
          </div>
        ) : (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring' }}
              className="inline-block p-4 bg-success-50 rounded-full mb-4"
            >
              <svg
                className="w-12 h-12 text-success-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </motion.div>
            <p className="text-lg font-semibold text-gray-800 mb-2">Clue Submitted!</p>
            <p className="text-gray-600">Waiting for other players...</p>
            <p className="text-sm text-gray-500 mt-4">
              {Object.keys(gameState.round.clues).length} /{' '}
              {Object.keys(gameState.players).length - 1} clues submitted
            </p>
          </div>
        )}

        {/* Tips */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-700 mb-2">Tips:</h3>
          <ul className="text-sm text-blue-600 space-y-1">
            <li>• Think of associations, not synonyms</li>
            <li>• Avoid words that others might also choose</li>
            <li>• Be creative but clear</li>
            <li>• Remember: duplicates will be removed!</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}

export default WritingPhase;
